import React, { useState } from 'react';

import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import { startCase, lowerCase } from 'lodash';
import { useBreakpoint } from 'use-breakpoint';

import MembershipSelectionTable from './MembershipSelectionTable';
import { CustomerMembershipTierEnum } from '../__generated__/graphql';
import PrimaryButton from '../components/PrimaryButton';
import Fonts, { fontFamily } from '../fonts';
import { BREAKPOINTS } from '../helpers';
import Icons from '../icons';
import { Membership } from '../patientOnboarding/patientOnboarding.helpers';
import useWebBookingStore from '../store';

interface Benefit {
    primary?: string;
    secondary?: string;
    icon: 'check' | 'x';
}

export interface Tier {
    tier: CustomerMembershipTierEnum
    title: string
    price: string
    discountedPrice?: string
    description: string
    customDescription?: string
    isPopular?: boolean
    benefits?: Benefit[]
}

type Props = {
    membershipPlans: Membership[];
    onSelectMembershipTier: (isDifferentTier: boolean)=> void;
};

const MembershipSelection = ({ membershipPlans, onSelectMembershipTier }: Props) => {
    const { patient, updatePatient, updateOnGoToPrevStep } = useWebBookingStore((state) => ({
        patient: state.patient,
        updatePatient: state.setPatient,
        updateOnGoToPrevStep: state.setOnGoToPrevStep
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';
    const [selectedTier, setSelectedTier] = useState<CustomerMembershipTierEnum | undefined>(
        patient.membershipTier === CustomerMembershipTierEnum.PayAsYouGo
            ? CustomerMembershipTierEnum.Essential
            : CustomerMembershipTierEnum.PayAsYouGo
    );
    const [isCompareTableOpen, setIsCompareTableOpen] = useState(false);

    const MEMBERSHIP_BENEFITS: Partial<Record<CustomerMembershipTierEnum, Benefit[]>> = {
        [CustomerMembershipTierEnum.AllAccess]: [
            { primary: 'Unlimited visits', secondary: 'with no exam fee', icon: 'check' },
            { secondary: 'Preferred access to sick urgent care when fully booked', icon: 'check' },
            { secondary: '24/7 virtual care including chat, phone & video', icon: 'check' },
        ],
        [CustomerMembershipTierEnum.Essential]: [
            { primary: '1 visit', secondary: 'with no exam fee', icon: 'check' },
            { secondary: 'Preferred access to sick urgent care when fully booked', icon: 'x' },
            { secondary: '24/7 virtual care including chat, phone & video', icon: 'check' },
        ],
        [CustomerMembershipTierEnum.PayAsYouGo]: [
            { secondary: 'Visits included with no exam fee', icon: 'x' },
            { secondary: 'Preferred access to sick urgent care when fully booked', icon: 'x' },
            { secondary: '24/7 virtual care including chat, phone & video', icon: 'x' },
        ],
    };

    const MEMBERSHIP_DESCRIPTIONS: Partial<Record<CustomerMembershipTierEnum, string>> = {
        [CustomerMembershipTierEnum.AllAccess]: 'Unlimited exams and priority care - peace of mind perfect for pets who are young, getting older, or just love to socialize.',
        [CustomerMembershipTierEnum.Essential]:
            '24/7 virtual care, plus 1 exam. A rainy day plan for low-maintenance pets who lead quiet, mostly indoor lives.',
        [CustomerMembershipTierEnum.PayAsYouGo]:
            'A simple option to try us out - but without 24/7 virtual care or priority access for those "just in case" moments.',
    };

    const getMembershipTiers: Tier[] = (membershipPlans.map((tier) => ({
        tier: tier.membershipTier,
        title: startCase(lowerCase(tier.membershipTier)),
        description: tier.description,
        customDescription: MEMBERSHIP_DESCRIPTIONS[tier.membershipTier],
        price: tier.membershipPrice,
        discountedPrice: tier.discountedPrice,
        isPopular: tier.membershipTier === CustomerMembershipTierEnum.AllAccess,
        benefits: MEMBERSHIP_BENEFITS[tier.membershipTier],
    })));

    const handleSubmit = () => {
        if (!selectedTier) return;
        updatePatient({ ...patient, membershipTier: selectedTier });
        updateOnGoToPrevStep(undefined);
        onSelectMembershipTier(patient.membershipTier !== selectedTier);
    };

    const onSelectTier = (tier: CustomerMembershipTierEnum) => {
        if (tier === selectedTier) {
            setSelectedTier(undefined);
        } else {
            setSelectedTier(tier);
        }
    };

    const renderButton = () => (
        <ButtonContainer isMobile={isMobile}>
            {isCompareTableOpen
                ? (
                    <PrimaryButton
                        color="periwinkle"
                        onClick={() => setIsCompareTableOpen(false)}
                    >
                        Return to plan selection
                    </PrimaryButton>
                )
                : (
                    <PrimaryButton color="periwinkle" onClick={handleSubmit} disabled={!selectedTier}>
                        Select plan
                    </PrimaryButton>
                )}
        </ButtonContainer>
    );

    const rank: Partial<Record<CustomerMembershipTierEnum, number>> = {
        [CustomerMembershipTierEnum.AllAccess]: 0,
        [CustomerMembershipTierEnum.Essential]: 1,
        [CustomerMembershipTierEnum.PayAsYouGo]: 2,
    };

    const getMembershipTiersOrdered = getMembershipTiers.sort(
        (a, b) => (rank[a.tier] ?? 999) - (rank[b.tier] ?? 999)
    );

    const renderBody = () => (
        <>
            {getMembershipTiersOrdered.map((tier) => {
                const isPayAsYouGo = tier.tier === CustomerMembershipTierEnum.PayAsYouGo;
                const hasDiscount = !isPayAsYouGo
                    && tier.discountedPrice
                    && tier.price !== tier.discountedPrice;
                return (
                    <TierCard
                        key={tier.tier}
                        isSelected={selectedTier === tier.tier}
                        onClick={() => onSelectTier(tier.tier)}
                    >
                        {tier.isPopular && <PopularBadge>Most popular</PopularBadge>}

                        <TierHeader>
                            <TierTitle>
                                {tier.title}
                                {' '}
                                -
                                {' '}
                                {hasDiscount ? (
                                    <>
                                        <PriceStrikeThrough>{`$${tier.price}`}</PriceStrikeThrough>
                                        {' '}
                                        <PriceHighlight>{`$${tier.discountedPrice}/year`}</PriceHighlight>
                                    </>
                                )
                                    : (
                                        <>
                                            {`$${tier.price}`}
                                            {tier.tier === CustomerMembershipTierEnum.PayAsYouGo ? ' per exam' : '/year'}
                                        </>
                                    )}
                            </TierTitle>
                        </TierHeader>

                        <TierDescription>{tier.customDescription}</TierDescription>

                        {tier.benefits && (
                            <BenefitsContainer
                                data-testid="benefits-container"
                                isOpen={selectedTier === tier.tier}
                                key={`${tier.tier}-benefits-container`}
                            >
                                <BlueDivider key={`${tier.tier}-divider`} />
                                <BenefitsContent key={`${tier.tier}-benefits-content`}>
                                    {tier.benefits.map((benefit) => (
                                        <React.Fragment key={`${tier.tier}-benefit-${benefit.primary}-${benefit.secondary}`}>
                                            <BenefitRow>
                                                <BenefitText isMobile={isMobile}>
                                                    {benefit.primary && (
                                                        <BenefitPrimaryText>
                                                            {benefit.primary}
                                                        </BenefitPrimaryText>
                                                    )}
                                                    {benefit.secondary && (
                                                        <BenefitSecondaryText>
                                                            {` ${benefit.secondary}`}
                                                        </BenefitSecondaryText>
                                                    )}
                                                </BenefitText>
                                                {benefit.icon === 'check' ? (
                                                    <Icons.Check
                                                        width={24}
                                                        height={24}
                                                    />
                                                ) : (
                                                    <Icons.X
                                                        width={24}
                                                        height={24}
                                                        fill={colors.RED}
                                                    />
                                                )}
                                            </BenefitRow>
                                            <BlueDivider />
                                        </React.Fragment>
                                    ))}
                                </BenefitsContent>
                            </BenefitsContainer>
                        )}
                    </TierCard>
                );
            })}

            <CompareButtonContainer>
                <CompareButton
                    type="button"
                    onClick={() => setIsCompareTableOpen(true)}
                >
                    Compare all plans side by side
                </CompareButton>
            </CompareButtonContainer>
        </>
    );

    return (
        <Container isMobile={isMobile}>
            <Fonts.Heading03 style={{ textAlign: 'center' }}>{`Select ${patient.name}'s plan`}</Fonts.Heading03>
            <Body>
                {!isCompareTableOpen && renderBody()}
                {isCompareTableOpen && (
                    <MembershipSelectionTable
                        membershipTiers={getMembershipTiers}
                        setIsCompareTableOpen={setIsCompareTableOpen}
                    />
                )}
            </Body>

            {renderButton()}
        </Container>
    );
};

// ts-prune-ignore-next-line
export default MembershipSelection;

const Container = styled.div<{ isMobile?: boolean }>`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    gap: 32px;
    ${({ isMobile }) => (isMobile ? 'padding: 32px 24px 0 24px' : 'padding: 32px 24px')};
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ButtonContainer = styled.div<{ isMobile?: boolean }>`
    display: flex;
    flex-direction: column;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    ${({ isMobile }) => (isMobile && 'padding: 16px 0')};
    bottom: 0px;
    background-color: ${colors.WHITE};
`;

const BlueDivider = styled.div`
    height: 1px;
    background-color: ${colors.PERIWINKLE_4};
`;

const TierCard = styled.div<{ isSelected: boolean }>`
    border: ${(props) => (props.isSelected ? `2px solid ${colors.PRIMARY_PERIWINKLE}` : '')};
    border-radius: 16px;
    padding: 16px;
    gap: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: ${colors.WHITE};
    box-shadow: 0px 2px 5px 0px rgba(212, 218, 220, 0.50);
`;

const PopularBadge = styled.div`
    background-color: ${colors.PERIWINKLE_4};
    color: ${colors.PRIMARY_PERIWINKLE};
    font-size: 13px;
    font-weight: 600;
    padding: 6px 7px;
    border-radius: 24px;
    width: fit-content;
    margin-bottom: 8px;
`;

const TierHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const TierTitle = styled.p`
    font-weight: 600;
    font-size: 20px;
    line-height: 28px;
    color: ${colors.GREY_1};
    ${fontFamily.baseMedium}
`;

const TierDescription = styled.p`
    font-family: ${fontFamily.base};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: ${colors.GREY_2};
    margin-top: 8px;
`;

const PriceHighlight = styled.span`
    color: ${colors.PRIMARY_PERIWINKLE};
`;

const PriceStrikeThrough = styled.span`
    text-decoration: line-through;
    color: ${colors.GREY_2};
`;

const BenefitsContainer = styled.div<{ isOpen: boolean }>`
    max-height: ${(props) => (props.isOpen ? '500px' : '0')};
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    margin-top: ${(props) => (props.isOpen ? '8px' : '0')};
`;

const BenefitsContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const BenefitRow = styled.div`
    display: flex;
    align-items: center;
    padding: 13px 0;
    justify-content: space-between;
`;

const BenefitText = styled.p<{ isMobile?: boolean }>`
    font-family: ${fontFamily.base};
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    ${({ isMobile }) => (isMobile && 'max-width: 260px')};
`;

const BenefitPrimaryText = styled.span`
    color: ${colors.PRIMARY_PERIWINKLE};
    font-weight: 700;
`;

const BenefitSecondaryText = styled.span`
    color: ${colors.GREY_1};
`;

const CompareButtonContainer = styled.div`
    display: flex;
    justify-content: right;
`;

const CompareButton = styled.button`
    border: 1px solid ${colors.PRIMARY_PERIWINKLE};
    color: ${colors.PRIMARY_PERIWINKLE};
    border-radius: 9999px;
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    background: ${colors.WHITE};
    cursor: pointer;
`;
